import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { VideoCapability, VideoQuality, VideoQualityProfile, STANDARD_RESOLUTIONS, SUPPORTED_VIDEO_CODECS, VIDEO_QUALITY_PROFILES } from './video.interface';

@Injectable({
  providedIn: 'root'
})
export class VideoDeviceService {
  private currentVideoInputDevice = new BehaviorSubject<string>('default');
  private availableVideoInputDevices = new BehaviorSubject<MediaDeviceInfo[]>([]);
  private videoCapability = new BehaviorSubject<VideoCapability | null>(null);

  constructor() {
    // Initialize device enumeration
    this.enumerateDevices();

    // Listen for device changes
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', () => {
        this.enumerateDevices();
      });
    }

    // Restore saved preference
    const savedDeviceId = localStorage.getItem('preferredCameraId');
    if (savedDeviceId) {
      this.currentVideoInputDevice.next(savedDeviceId);
    }
  }

  /**
   * Enumerate available video input devices
   */
  private async enumerateDevices(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputDevices = devices.filter(device => device.kind === 'videoinput');
      this.availableVideoInputDevices.next(videoInputDevices);

      // If no devices available, update capability
      if (videoInputDevices.length === 0) {
        this.videoCapability.next({
          hasCamera: false,
          hasVideoCodec: false,
          supportedResolutions: [],
          recommendedQuality: 'sd',
          availableCameras: []
        });
      }
    } catch (error) {
      console.error('Error enumerating video devices:', error);
      this.availableVideoInputDevices.next([]);
    }
  }

  /**
   * Get available video input devices
   */
  getAvailableVideoInputDevices(): Observable<MediaDeviceInfo[]> {
    return this.availableVideoInputDevices.asObservable();
  }

  /**
   * Get current video input device
   */
  getCurrentVideoInputDevice(): Observable<string> {
    return this.currentVideoInputDevice.asObservable();
  }

  /**
   * Get current video input device value (synchronous)
   */
  getCurrentVideoInputDeviceValue(): string {
    return this.currentVideoInputDevice.value;
  }

  /**
   * Set video input device
   */
  setVideoInputDevice(deviceId: string): void {
    this.currentVideoInputDevice.next(deviceId);
    localStorage.setItem('preferredCameraId', deviceId);
  }

  /**
   * Detect video capabilities including codec support and camera availability
   */
  async detectVideoCapabilities(): Promise<VideoCapability> {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          hasCamera: false,
          hasVideoCodec: false,
          supportedResolutions: [],
          recommendedQuality: 'sd',
          availableCameras: []
        };
      }

      // Enumerate video devices
      await this.enumerateDevices();
      const cameras = this.availableVideoInputDevices.value;

      if (cameras.length === 0) {
        return {
          hasCamera: false,
          hasVideoCodec: false,
          supportedResolutions: [],
          recommendedQuality: 'sd',
          availableCameras: []
        };
      }

      // Check codec support
      const hasVideoCodec = await this.checkVideoCodecSupport();

      // Don't test resolutions with getUserMedia - assume all standard resolutions are supported
      // This avoids requesting camera permission during capability detection
      const supportedResolutions = STANDARD_RESOLUTIONS;

      // Determine recommended quality based on camera count and codec support
      // Default to HD for modern browsers with codec support
      let recommendedQuality: VideoQuality = hasVideoCodec ? 'hd' : 'sd';

      const capability: VideoCapability = {
        hasCamera: true,
        hasVideoCodec,
        supportedResolutions,
        recommendedQuality,
        availableCameras: cameras
      };

      this.videoCapability.next(capability);
      return capability;

    } catch (error) {
      console.error('Error detecting video capabilities:', error);
      return {
        hasCamera: false,
        hasVideoCodec: false,
        supportedResolutions: [],
        recommendedQuality: 'sd',
        availableCameras: []
      };
    }
  }

  /**
   * Check if browser supports required video codecs
   */
  private async checkVideoCodecSupport(): Promise<boolean> {
    if (!('RTCPeerConnection' in window)) {
      return false;
    }

    try {
      // Check for codec support via RTCRtpSender
      if ('RTCRtpSender' in window && 'getCapabilities' in RTCRtpSender) {
        const capabilities = RTCRtpSender.getCapabilities('video');
        if (capabilities && capabilities.codecs) {
          // Check if any of our preferred codecs are supported
          const supportedCodecs = capabilities.codecs.map(c => c.mimeType);
          return SUPPORTED_VIDEO_CODECS.some(codec =>
            supportedCodecs.includes(codec)
          );
        }
      }

      // Fallback: assume support if RTCPeerConnection exists
      return true;
    } catch (error) {
      console.warn('Error checking codec support:', error);
      return true; // Assume support
    }
  }

  /**
   * Test which resolutions are supported by the camera
   */
  private async testSupportedResolutions(): Promise<typeof STANDARD_RESOLUTIONS> {
    const supported = [];

    for (const resolution of STANDARD_RESOLUTIONS) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: resolution.width },
            height: { ideal: resolution.height }
          }
        });

        // Check actual resolution
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings();

        // Stop the track immediately
        track.stop();

        // Consider it supported if we got close to the requested resolution
        if (settings.width && settings.width >= resolution.width * 0.9) {
          supported.push(resolution);
        }
      } catch (error) {
        // This resolution is not supported, continue
        console.debug(`Resolution ${resolution.label} not supported`);
      }
    }

    // If no specific resolutions were detected, return SD as fallback
    if (supported.length === 0) {
      supported.push(STANDARD_RESOLUTIONS[1]); // VGA
    }

    return supported;
  }

  /**
   * Get video stream with specified quality
   */
  async getVideoStream(quality: VideoQuality = 'auto', deviceId?: string): Promise<MediaStream> {
    const profile = VIDEO_QUALITY_PROFILES[quality];
    const currentDeviceId = deviceId || this.currentVideoInputDevice.value;

    const constraints: MediaStreamConstraints = {
      video: {
        deviceId: currentDeviceId === 'default' ? undefined : { exact: currentDeviceId },
        width: { ideal: profile.width },
        height: { ideal: profile.height },
        frameRate: { ideal: profile.maxFramerate },
        aspectRatio: { ideal: profile.aspectRatio }
      }
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // Re-enumerate devices after successful getUserMedia (first-time permission grant reveals full device list)
      this.enumerateDevices();
      return stream;
    } catch (error) {
      console.error('Error getting video stream:', error);

      // Try with less strict constraints
      const fallbackConstraints: MediaStreamConstraints = {
        video: true
      };

      const stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
      // Re-enumerate devices after successful getUserMedia
      this.enumerateDevices();
      return stream;
    }
  }

  /**
   * Get combined audio and video stream
   */
  async getAudioVideoStream(
    videoQuality: VideoQuality = 'auto',
    audioDeviceId?: string,
    videoDeviceId?: string
  ): Promise<MediaStream> {
    const profile = VIDEO_QUALITY_PROFILES[videoQuality];
    const currentVideoDeviceId = videoDeviceId || this.currentVideoInputDevice.value;

    const constraints: MediaStreamConstraints = {
      audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
      video: {
        deviceId: currentVideoDeviceId === 'default' ? undefined : { exact: currentVideoDeviceId },
        width: { ideal: profile.width },
        height: { ideal: profile.height },
        frameRate: { ideal: profile.maxFramerate },
        aspectRatio: { ideal: profile.aspectRatio }
      }
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // Re-enumerate devices after successful getUserMedia (first-time permission grant reveals full device list)
      this.enumerateDevices();
      return stream;
    } catch (error) {
      console.error('Error getting audio/video stream:', error);
      throw error;
    }
  }

  /**
   * Get video capability observable
   */
  getVideoCapability(): Observable<VideoCapability | null> {
    return this.videoCapability.asObservable();
  }

  /**
   * Request camera permission
   */
  async requestCameraPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the stream immediately after getting permission
      stream.getTracks().forEach(track => track.stop());
      // Re-enumerate devices after successful permission grant (reveals full device list with labels)
      this.enumerateDevices();
      return true;
    } catch (error) {
      console.error('Camera permission denied:', error);
      return false;
    }
  }

  /**
   * Check if camera permission is granted
   */
  async checkCameraPermission(): Promise<PermissionState | 'unsupported'> {
    if (!navigator.permissions || !navigator.permissions.query) {
      return 'unsupported';
    }

    try {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
      return result.state;
    } catch (error) {
      return 'unsupported';
    }
  }
}
